import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Users, ExternalLink } from "lucide-react";
import { BOOKS, WORKBOOKS } from "../../data/publicSite";
import usePageMeta from "../../lib/usePageMeta";

export default function BooksIndex() {
  usePageMeta({
    title: "Books & Resources | California Corporate Chaplains",
    description: "Books, workbooks, and companion guides from California Corporate Chaplains — resources for organizations supporting employees, patients, residents, caregivers, families, and teams through serious illness, grief, and loss.",
  });
  const featured = BOOKS[0];
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20" data-testid="books-index">
      <div className="max-w-2xl mb-12">
        <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">Books & Resources</p>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight text-slate-900">
          Professional resources offered through California Corporate Chaplains.
        </h1>
        <p className="mt-4 text-lg text-slate-600 leading-relaxed">
          Books, workbooks, and companion guides that organizations use to support employees, patients, residents, caregivers, families, and teams through serious illness, grief, and loss.
        </p>
      </div>

      {/* Featured book */}
      <section className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-start bg-[#F8FAFC] border border-slate-200 rounded-2xl p-6 md:p-10 mb-16" data-testid="featured-book">
        <div className="lg:col-span-2">
          <div
            className="aspect-[3/4] rounded-xl bg-white border border-slate-200 shadow-md flex items-center justify-center p-4 sm:p-5"
            data-testid="featured-book-cover"
          >
            {featured.coverImage ? (
              <img
                src={featured.coverImage}
                alt={featured.coverAlt || `Front cover of ${featured.title} by ${featured.author}.`}
                loading="eager"
                className="w-full h-full object-contain"
              />
            ) : (
              <div className="w-full h-full rounded-lg bg-gradient-to-br from-[#4A6FA5] to-[#3B597B] flex flex-col items-center justify-center text-white p-8">
                <BookOpen className="w-8 h-8 mb-6 opacity-80" strokeWidth={1.5} />
                <p className="text-[10px] uppercase tracking-[0.3em] opacity-70 mb-3 text-center">Featured book</p>
                <h3 className="font-heading text-2xl font-medium text-center leading-tight">{featured.title}</h3>
                <p className="mt-3 text-sm opacity-90 text-center leading-relaxed italic">{featured.subtitle}</p>
                <p className="mt-4 text-xs opacity-80 text-center">by {featured.author}</p>
              </div>
            )}
          </div>
        </div>
        <div className="lg:col-span-3">
          <p className="text-xs uppercase tracking-[0.2em] text-[#4A6FA5] font-semibold mb-3">Featured</p>
          <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-slate-900">{featured.title}</h2>
          <p className="mt-2 text-slate-600 italic">{featured.subtitle}</p>
          <p className="mt-2 text-sm text-slate-500">by {featured.author}</p>
          <p className="mt-5 text-sm text-slate-700 leading-relaxed">{featured.description}</p>
          <div className="mt-6 flex flex-wrap gap-2">
            {featured.audiencesGrid.map((a) => (
              <span key={a} className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-white border border-slate-200 text-slate-700">{a}</span>
            ))}
          </div>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a
              href={featured.amazonUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="featured-book-amazon"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B]"
            >
              Purchase on Amazon <ExternalLink className="w-4 h-4" />
            </a>
            <Link
              to={`/books/${featured.slug}`}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg border border-slate-300 text-slate-700 font-medium hover:border-[#4A6FA5] hover:text-[#4A6FA5]"
            >
              Learn more <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="mt-3">
            <Link
              to={`/bulk-order?product=${featured.slug === "from-diagnosis-to-goodbye" ? "from_diagnosis_to_goodbye" : "multiple"}`}
              data-testid="featured-book-bulk"
              className="inline-flex items-center gap-2 text-sm font-medium text-[#4A6FA5] hover:text-[#3B597B]"
            >
              Request a Bulk / Organizational Order <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Workbooks */}
      <section id="workbooks" className="scroll-mt-24 mb-16">
        <div className="max-w-2xl mb-8">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">Guided Workbooks</p>
          <h2 className="font-heading text-3xl tracking-tight text-slate-900">Companion workbooks for hospice, bereavement, and family conversation.</h2>
          <p className="mt-3 text-slate-600 leading-relaxed">
            Structured, guided companions designed to be used alongside the book — individually, with families, or in small groups and team settings.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {WORKBOOKS.map((w) => (
            <div key={w.slug} className="bg-white border border-slate-200 rounded-2xl p-6 md:p-7 flex flex-col" data-testid={`workbook-${w.slug}`}>
              <div className="flex items-center justify-between">
                <BookOpen className="w-5 h-5 text-[#4A6FA5]" strokeWidth={1.75} />
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-[0.14em] bg-green-50 text-green-700 border border-green-100">
                  {w.status}
                </span>
              </div>
              <h3 className="mt-4 font-heading text-lg font-medium text-slate-900 leading-snug">{w.title}</h3>
              <p className="mt-1 text-sm text-slate-600 italic leading-relaxed">{w.subtitle}</p>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed flex-1">{w.body}</p>
              <div className="mt-5 flex flex-col sm:flex-row gap-2">
                <a
                  href={w.amazonUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`workbook-amazon-${w.slug}`}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#4A6FA5] text-white text-sm font-medium hover:bg-[#3B597B]"
                >
                  Purchase on Amazon <ExternalLink className="w-3.5 h-3.5" />
                </a>
                <Link
                  to={`/bulk-order?product=${w.slug === "hospice-workbook" ? "hospice_workbook" : "bereavement_workbook"}`}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:border-[#4A6FA5] hover:text-[#4A6FA5]"
                >
                  Bulk order
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Organizational orders */}
      <section id="organizations" className="scroll-mt-24 rounded-2xl bg-[#4A6FA5] text-white p-8 md:p-12" data-testid="org-orders">
        <div className="grid lg:grid-cols-3 gap-8 items-center">
          <div className="lg:col-span-2">
            <p className="text-xs uppercase tracking-[0.2em] text-blue-200 font-semibold mb-3">Organizational orders</p>
            <h2 className="font-heading text-2xl sm:text-3xl tracking-tight">Bulk pricing, training bundles, and workshop packages.</h2>
            <p className="mt-4 text-blue-100 leading-relaxed">
              Hospices, healthcare organizations, senior-care providers, employers, and educational programs regularly use these books and workbooks in team training, patient care, and grief support. Contact us for bulk pricing, training bundles, and companion workshops tailored to your team.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <Link to="/bulk-order?product=multiple" className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-white text-[#4A6FA5] font-medium hover:bg-blue-50">
              Request a Bulk / Organizational Order <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/services/training-workshops" className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg border border-blue-200 text-white font-medium hover:bg-[#3B597B]">
              <Users className="w-4 h-4" /> Workshop inquiries
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
